import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Zap, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";

const fallbackCover =
  "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=1200&q=80";

export default function BlogDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    api
      .get(`/blog/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="bg-slate-950 min-h-screen text-white grid place-items-center">
        <div className="text-center">
          <p className="text-slate-400">Artikel tidak ditemukan.</p>
          <Link
            to="/blog"
            className="text-sky-400 font-semibold mt-3 inline-block"
          >
            ← Kembali ke Blog
          </Link>
        </div>
      </div>
    );

  const post = data?.post;
  const related = data?.related || [];

  return (
    <div
      className="bg-slate-950 min-h-screen text-white"
      data-testid="blog-detail-page"
    >
      {post && (
        <Seo
          title={`${post.title} — Blog RESADA`}
          description={post.excerpt}
          path={`/blog/${slug}`}
          image={post.cover_url || fallbackCover}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.cover_url || fallbackCover,
            datePublished: post.created_at || new Date().toISOString(),
            dateModified: post.updated_at || post.created_at || new Date().toISOString(),
            author: { "@type": "Organization", name: post.author || "RESADA", url: "https://resada.id" },
            publisher: { "@type": "Organization", name: "RESADA", logo: { "@type": "ImageObject", url: "https://resada.id/logo.png" } },
            mainEntityOfPage: { "@type": "WebPage", "@id": `https://resada.id/blog/${slug}` },
          }}
        />
      )}
      <style>{`
        .blog-content { color: #cbd5e1; font-size: 1.075rem; line-height: 1.85; }
        .blog-content h2 { color:#fff; font-family: 'Sora', sans-serif; font-weight:800; font-size:1.7rem; margin:2.2rem 0 0.9rem; }
        .blog-content h3 { color:#e2e8f0; font-weight:700; font-size:1.3rem; margin:1.6rem 0 0.6rem; }
        .blog-content p { margin: 0 0 1.1rem; }
        .blog-content ul, .blog-content ol { margin: 0 0 1.2rem 1.3rem; }
        .blog-content ul { list-style: disc; } .blog-content ol { list-style: decimal; }
        .blog-content li { margin-bottom: 0.5rem; }
        .blog-content a { color:#38bdf8; text-decoration: underline; }
        .blog-content blockquote { border-left:3px solid #0ea5e9; background:rgba(14,165,233,.08); padding:1rem 1.25rem; margin:1.6rem 0; border-radius:0 .75rem .75rem 0; color:#e2e8f0; font-style:italic; }
        .blog-content strong { color:#fff; }
      `}</style>

      <nav className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-6 lg:px-12 py-5">
        <Link to="/" className="flex items-center gap-2 text-white">
          <Logo to={null} dark iconSize={34} />
        </Link>
        <Link
          to="/blog"
          className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl font-semibold text-sm transition inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" /> Semua Artikel
        </Link>
      </nav>

      {!post ? (
        <div className="pt-40 max-w-3xl mx-auto px-6 space-y-4">
          <div className="h-10 bg-white/5 rounded animate-pulse w-3/4" />
          <div className="h-64 bg-white/5 rounded-2xl animate-pulse" />
        </div>
      ) : (
        <>
          {/* Cinematic hero with cover */}
          <header className="relative h-[60vh] min-h-[420px] flex items-end overflow-hidden">
            <img
              src={post.cover_url || fallbackCover}
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
            <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 pb-12 w-full">
              <span className="text-sky-400 text-xs font-bold uppercase tracking-widest">
                {post.category}
              </span>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3 leading-tight">
                {post.title}
              </h1>
              <div className="flex items-center gap-4 mt-5 text-sm text-slate-300">
                <span>{post.author}</span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {post.read_time} menit baca
                </span>
              </div>
            </div>
          </header>

          <article className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
            <p className="text-xl text-slate-200 font-light leading-relaxed border-l-2 border-sky-500 pl-5 mb-10">
              {post.excerpt}
            </p>
            <div
              className="blog-content"
              data-testid="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* CTA */}
            <div className="mt-14 rounded-3xl bg-gradient-to-br from-sky-600 to-sky-500 p-8 text-center">
              <h3 className="font-display text-2xl font-extrabold">
                Siap ubah artikel jadi aksi?
              </h3>
              <p className="text-sky-50 mt-2">
                Mulai kumpulkan ribuan lead & otomasi penjualanmu bersama
                RESADA.
              </p>
              <Link to="/register">
                <button className="mt-5 bg-white text-sky-600 font-bold px-6 py-3 rounded-xl inline-flex items-center gap-2 hover:scale-105 transition-transform">
                  Coba Gratis <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </article>

          {related.length > 0 && (
            <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-16">
              <h3 className="font-display text-2xl font-extrabold mb-6">
                Artikel Terkait
              </h3>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    data-testid="blog-related-card"
                    className="group rounded-2xl overflow-hidden bg-white/5 hover:bg-white/[0.08] border border-white/5 transition"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={p.cover_url || fallbackCover}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold leading-snug group-hover:text-sky-300 transition">
                        {p.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      <SiteFooter />
    </div>
  );
}
